/**
 * The order detail of handover rev7 §11.7: "Timeline, versions with diffs,
 * change requests, outbox history, print views."
 *
 * Two of the actions on it are payments': mark the transfer received, and
 * open the invoices. Logging and applying a change, waiving a fee and
 * building a new version are §11.8 and §11.11, and they are the staff
 * editor's own screen — this view links to it and lists the requests, rather
 * than growing a second editor beside it.
 */

import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { km } from '@plt/emails'

import { consoleCall } from '../api.ts'

/** Today in Budapest, as the date input wants it (handover §10). */
function todayInBudapest(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}
import { useConsole } from '../context.ts'

import {
  asDate,
  asMoment,
  fmt,
  Loading,
  REVIEW_REASON_HU,
  statusText,
  Tags,
  useConsoleAction,
  useConsoleData,
} from '../common.tsx'

interface Detail {
  order: {
    /** SimplePay test (20261103): a SimplePay test order. */
    is_test?: boolean
    id: string
    ref: string
    status: string
    status_reason: string | null
    lane: string
    version: number
    event_name: string | null
    event_date: string
    event_start: string | null
    guests: number
    gf_guests: number
    /** Run 17 item B11(c): the meal answer, where there is one to say. */
    occasion?: string | null
    occasion_label?: string | null
    meal_note?: string | null
    /** Run 19 §17: *Szállításra összekészítve*, default or override. */
    ready_by?: string | null
    ready_by_overridden?: boolean
    ready_by_set_by?: { name?: string | null; email?: string | null } | null
    ready_by_set_at?: string | null
    /** Run 19 §18: how the food leaves the kitchen. */
    packing_mode?: string | null
    packing_label?: string | null
    /** Run 19 §16: the machine record. Internal (§5.11). */
    coffee_cups?: number | null
    coffee_machines?: number | null
    coffee_machine_cost_net?: number | null
    coffee_machine_charge_net?: number | null
    coffee_charge_net?: number | null
    fulfilment: string
    slot_label: string | null
    zone: string | null
    /** Specification §3.7: the distance beside the zone. */
    distance_km?: number | string | null
    customer_name: string
    customer_company: string | null
    customer_email: string | null
    customer_phone: string | null
    is_business: boolean
    gross: number
    review_reasons: string[]
    dietary_note: string | null
    labels_blocked: boolean
    freeze_at: string | null
    payment_deadline: string | null
    created_at: string
    /** Run 13a item 2: an expired transfer order still takes a late transfer. */
    late_transfer_open?: boolean
  }
  net: number
  vat: number
  gross: number
  delivery_fee: number
  billing_address: Record<string, string> | null
  delivery_address: Record<string, string> | null
  /** szamlazz_advance (DECIDED 20261101): szamlazz.hu issues this order's
   *  előlegszámlák, so a transfer receipt takes the number too. */
  szamlazz_issues_advance?: boolean
  lines: {
    dish_id: string
    name_hu: string | null
    ordered_portions: number
    unit: string | null
    is_gf_portion: boolean
    /** Run 17 item B5(b): the gluten-free guests' share of this line. */
    gf_portions?: number | null
  }[]
  versions: {
    version: number
    source: string
    reason: string | null
    created_at: string
    superseded_at: string | null
    is_current: boolean
    created_by: { name?: string } | null
    diff: unknown
  }[]
  timeline: {
    at: string
    from_status: string | null
    to_status: string
    reason: string | null
    event_code: string
    actor: { kind: string; name?: string; email?: string }
  }[]
  outbox: {
    id: number
    event_code: string
    recipient_role: string | null
    to_email: string | null
    subject: string | null
    status: string
    attempts: number
    sent_at: string | null
    error: string | null
    created_at: string
    /** Run 12 item 2d: the provider's message ID (Brevo's, or `log:` for the log driver). */
    provider_msg_id?: string | null
  }[]
  payments: {
    id: string
    kind: string
    provider: string
    provider_ref: string | null
    amount: number
    status: string
    due_at: string | null
    done_at: string | null
    note: string | null
    created_at: string
    /** Card payment change 4 (20261101): `transfer` or `card_dijbekero`. */
    method?: string | null
    /** The előlegszámla that documents this payment, where there is one. */
    szamlazz_number?: string | null
  }[]
  invoices: {
    id: string
    kind: string
    szamlazz_number: string | null
    gross: number | null
    issued_at: string | null
    status: string
    error: string | null
    /** Item 54: where the PDF is in the private bucket. Null while the Agent
     *  has not answered, or where it answered without one. */
    pdf_path: string | null
    /** Card payment change 4: `agent`, or `szamlazz` for one szamlazz.hu issued. */
    issued_by?: string
    verification?: string | null
    verification_note?: string | null
  }[]
  change_requests: { id: string; kind: string; status: string; received_at: string }[]
  dietary_signoffs: {
    id: string
    decision: string
    resolution: string
    at: string
    signed_by: { name?: string }
  }[]
}

export function OrderDetail(): JSX.Element {
  const { orderId = '' } = useParams()
  const { data, error, loading, reload } = useConsoleData<Detail>('order_detail', {
    order_id: orderId,
  })
  const { run, busy, error: actionError, moved } = useConsoleAction()
  const { config, session } = useConsole()
  const [pdfBusy, setPdfBusy] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  /**
   * Item 54: open a stored invoice PDF, through the console and by no other
   * route. The endpoint answers with the bytes, not with a link — there is no
   * URL to copy — and the blob the browser makes lives for as long as the tab
   * does and no longer.
   */
  async function openInvoicePdf(invoiceId: string): Promise<void> {
    setPdfBusy(invoiceId)
    setPdfError(null)
    try {
      const answer = await consoleCall<{ base64: string; filename: string; content_type: string }>(
        'invoice_pdf',
        { invoice_id: invoiceId },
        { config, session },
      )
      const binary = atob(answer.base64)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
      const url = URL.createObjectURL(new Blob([bytes], { type: answer.content_type }))
      const link = document.createElement('a')
      link.href = url
      link.download = answer.filename
      link.rel = 'noopener'
      link.click()
      // Revoked on the next turn of the loop: the download has the handle by
      // then, and an object URL left behind is a document kept in memory.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch {
      setPdfError('A bizonylat PDF-je most nem nyitható meg.')
    } finally {
      setPdfBusy(null)
    }
  }
  const [amount, setAmount] = useState('')
  const [bankRef, setBankRef] = useState('')
  // build/FIXES.md item 85: the day the bank credited the transfer, which is
  // the előlegszámla's tax point. Today unless staff say otherwise, and never
  // later than today -- the form says so and the database refuses it anyway.
  const today = todayInBudapest()
  const [creditDate, setCreditDate] = useState(today)
  // Card payment change 4 (DECIDED 20261101): the receipt names how the money
  // came. A card payment on the díjbekérő carries the szamlazz.hu előlegszámla
  // number, and the system issues no előlegszámla of its own for it.
  const [method, setMethod] = useState<'' | 'transfer' | 'card_dijbekero'>('')
  const [advanceNumber, setAdvanceNumber] = useState('')
  // build/FIXES.md item 86: the number of a held végszámla Finance issued by hand.
  const [handNumber, setHandNumber] = useState<Record<string, string>>({})

  // Run 12 item G: a staff email may link to one section of this page
  // (#penzmozgas, #szamlak, #levelek, #modositasok). The section exists only
  // once the order has loaded, so the scroll waits for it.
  const { hash } = useLocation()
  const loaded = data !== null
  useEffect(() => {
    if (!loaded || hash.length < 2) return
    const target = globalThis.document?.getElementById(decodeURIComponent(hash.slice(1)))
    target?.scrollIntoView({ block: 'start' })
  }, [loaded, hash])

  if (data === null) {
    return (
      <section data-testid="view-order-detail">
        <Loading loading={loading} error={error} />
      </section>
    )
  }

  const order = data.order
  // Run 13a item 2 (DECIDED 20261012): an expired transfer order takes a late
  // transfer too; the server sends it for confirmation (late_payment).
  const lateTransfer = order.late_transfer_open === true
  // szamlazz_advance (DECIDED 20261101): on the díjbekérő route a transfer
  // receipt carries the szamlazz.hu előlegszámla number, as a card one does.
  const numberNeeded = data.szamlazz_issues_advance === true
  // SimplePay test prompt §2: a test order has no invoicing actions — no
  // receipt that would record an előlegszámla, no végszámla by hand. The
  // invoice runner and the drivers refuse its jobs anyway.
  const testOrder = order.is_test === true
  const canMarkTransfer =
    !testOrder && order.lane === 'transfer' && (order.status === 'awaiting_payment' || lateTransfer)

  return (
    <section data-testid="view-order-detail">
      <h2 data-testid="detail-ref">
        {order.ref}
        {testOrder && (
          <span className="tag stop" data-testid="detail-test" style={{ marginLeft: 8 }}>TESZT</span>
        )}
      </h2>
      {testOrder && (
        <p className="note" data-testid="detail-test-note">
          SimplePay teszt rendelés: számla nem készül, a konyha nem kap feladatot, a kapacitásba nem
          számít. Lemondáskor sem készül sztornó.
        </p>
      )}
      <p className="lead">
        {statusText(order.status, order.status_reason)} · {order.version}. verzió ·{' '}
        {order.lane === 'transfer' ? 'átutalás' : 'bankkártya'}
      </p>

      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      <div className="grid">
        <article className="card">
          <h3>Rendezvény</h3>
          <dl className="facts">
            <dt>Dátum</dt>
            <dd>{asDate(order.event_date, true)}</dd>
            <dt>Kezdés</dt>
            <dd>{order.event_start?.slice(0, 5) ?? '—'}</dd>
            <dt>Vendégek</dt>
            <dd>
              {fmt.n(order.guests)} fő
              {order.gf_guests > 0 ? ` · ${fmt.n(order.gf_guests)} gluténmentes` : ''}
            </dd>
            {/* Run 17 item B11(c), as run 19 §4 extends it: the meal note
                in either answer, here as on every other surface. */}
            {typeof order.meal_note === 'string' && order.meal_note !== '' ? (
              <>
                <dt>Étkezés</dt>
                <dd data-mealnote="light">{order.meal_note}</dd>
              </>
            ) : null}
            {/* Run 19 §17 (EXPERT, Balázs 20261016): the ready-for-dispatch
                time — the default, or the one a colleague set by hand, with
                who set it and when. Never shown to a customer. */}
            {typeof order.ready_by === 'string' && order.ready_by !== '' ? (
              <>
                <dt>Szállításra összekészítve</dt>
                <dd data-readyby={order.ready_by_overridden === true ? 'manual' : 'default'}>
                  {asMoment(order.ready_by)}
                  {order.ready_by_overridden === true
                    ? ` · kézzel beállítva${
                        order.ready_by_set_by?.name ? `: ${order.ready_by_set_by.name}` : ''
                      }${order.ready_by_set_at ? `, ${asMoment(order.ready_by_set_at)}` : ''}`
                    : ''}
                </dd>
              </>
            ) : null}
            {/* Run 19 §18: how the food leaves the kitchen, which follows
                the staff answer on every occasion. */}
            {typeof order.packing_label === 'string' && order.packing_label !== '' ? (
              <>
                <dt>Csomagolás</dt>
                <dd data-packing={order.packing_mode ?? undefined}>{order.packing_label}</dd>
              </>
            ) : null}
            {/* Run 19 §16: the machine record. The cost and the charge are
                internal (§5.11) and are here so a month's report can be read
                off the orders rather than reconstructed. */}
            {typeof order.coffee_cups === 'number' && order.coffee_cups > 0 ? (
              <>
                <dt>Kávé</dt>
                <dd data-coffee="yes">
                  {fmt.n(order.coffee_cups)} csésze
                  {typeof order.coffee_machines === 'number' && order.coffee_machines > 0
                    ? ` · ${fmt.n(order.coffee_machines)} kávégép`
                    : ''}
                  {typeof order.coffee_machine_cost_net === 'number'
                    ? ` · gépbérlet ${fmt.money(order.coffee_machine_cost_net)} nettó költség`
                    : ''}
                  {typeof order.coffee_machine_charge_net === 'number'
                    ? ` / ${fmt.money(order.coffee_machine_charge_net)} nettó bevétel`
                    : ''}
                </dd>
              </>
            ) : null}
            <dt>Teljesítés</dt>
            <dd>
              {order.fulfilment === 'pickup' ? 'Személyes átvétel' : 'Kiszállítás'}
              {order.slot_label === null ? '' : ` · ${order.slot_label}`}
              {order.zone === null ? '' : ` · ${order.zone} zóna`}
              {km(order.distance_km) === '' ? '' : ` · ${km(order.distance_km)}`}
            </dd>
            <dt>Véglegesítés</dt>
            <dd>{asMoment(order.freeze_at)}</dd>
          </dl>
        </article>

        <article className="card">
          <h3>Vendég</h3>
          <dl className="facts">
            <dt>Név</dt>
            <dd>{order.customer_name}</dd>
            <dt>Cég</dt>
            <dd>{order.customer_company ?? '—'}</dd>
            <dt>E-mail</dt>
            <dd>{order.customer_email ?? '—'}</dd>
            <dt>Telefon</dt>
            <dd>{order.customer_phone ?? '—'}</dd>
            <dt>Számlázás</dt>
            <dd>
              {data.billing_address === null ? '—' : [
                data.billing_address.postcode,
                data.billing_address.town,
                data.billing_address.street_and_number,
              ].filter(Boolean).join(' ')}
            </dd>
          </dl>
        </article>

        <article className="card">
          <h3>Összeg</h3>
          <dl className="facts">
            <dt>Nettó</dt>
            <dd>{fmt.money(data.net)}</dd>
            <dt>Áfa</dt>
            <dd>{fmt.money(data.vat)}</dd>
            <dt>Bruttó</dt>
            <dd>{fmt.money(data.gross)}</dd>
            <dt>Szállítás</dt>
            <dd>{fmt.money(data.delivery_fee)}</dd>
          </dl>
        </article>
      </div>

      {order.review_reasons.length > 0 && (
        <p className="note" data-testid="review-reasons">
          Jóváhagyási ok:{' '}
          <Tags values={order.review_reasons.map((r) => REVIEW_REASON_HU[r] ?? r)} />
        </p>
      )}

      {order.dietary_note !== null && (
        <article className="card" data-testid="detail-dietary">
          <h3>Étrendi megjegyzés</h3>
          <p>{order.dietary_note}</p>
          {data.dietary_signoffs.length === 0
            ? <p className="note gap">Aláírás még nincs (§11.4).</p>
            : (
              <ul className="timeline">
                {data.dietary_signoffs.map((signoff) => (
                  <li key={signoff.id}>
                    <time>{asMoment(signoff.at)}</time>
                    <strong>{signoff.decision === 'signed' ? 'Jóváhagyva' : 'Elutasítva'}</strong>
                    {' — '}
                    {signoff.resolution} · {signoff.signed_by?.name ?? ''}
                  </li>
                ))}
              </ul>
            )}
        </article>
      )}

      <article className="card">
        <h3>Nyomtatási nézetek (§11.6)</h3>
        <div className="actions">
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K1`}
            target="_blank"
            data-testid="print-k1"
          >
            <button type="button">K1 · gyártási rendelés</button>
          </Link>
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K2`}
            target="_blank"
            data-testid="print-k2"
          >
            <button type="button">K2 · gyártási rendelés, soron kívül</button>
          </Link>
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K3`}
            target="_blank"
            data-testid="print-k3"
          >
            <button type="button">K3 · módosítás véglegesítés után</button>
          </Link>
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K4`}
            target="_blank"
            data-testid="print-k4"
          >
            <button type="button">K4 · lemondás véglegesítés után</button>
          </Link>
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K6`}
            target="_blank"
            data-testid="print-k6"
          >
            <button type="button">K6 · csomagolási lista és címkék</button>
          </Link>
          <Link
            to={`/staff/orders/${order.id}/nyomtatas/K8`}
            target="_blank"
            data-testid="print-k8"
          >
            <button type="button">K8 · étrendi megjegyzés</button>
          </Link>
        </div>
        {order.labels_blocked && (
          <p className="note gap">A címkézés blokkolva van, címke nem nyomtatható (§11.6).</p>
        )}
      </article>

      <article className="card">
        <h3>Tételek</h3>
        <table>
          <thead>
            <tr>
              <th>Fogás</th>
              <th className="num">Adag</th>
              <th>Gluténmentes</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, index) => (
              <tr key={`${line.dish_id}-${index}`}>
                <td>{line.name_hu ?? line.dish_id}</td>
                <td className="num">{fmt.n(line.ordered_portions)} {line.unit ?? 'db'}</td>
                <td data-gfshare={line.dish_id}>
                  {/* Run 17 item B5(b): a dish already on the menu carries
                      the gluten-free guests' share inside its own quantity,
                      and says how much. */}
                  {line.is_gf_portion
                    ? 'külön dobozban'
                    : (line.gf_portions ?? 0) > 0
                      ? `ebből ${fmt.n(line.gf_portions ?? 0)} db gluténmentes vendégeknek`
                      : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>

      <div className="grid">
        <article className="card">
          <h3>Idővonal</h3>
          <ul className="timeline" data-testid="timeline">
            {data.timeline.map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <time>{asMoment(event.at)}</time>
                {statusText(event.to_status, event.reason)}
                <span className="tag">{event.event_code}</span>
                <span className="tag">
                  {event.actor.kind === 'staff'
                    ? event.actor.name ?? event.actor.email ?? 'munkatárs'
                    : event.actor.kind === 'customer'
                    ? 'vendég'
                    : 'rendszer'}
                </span>
              </li>
            ))}
          </ul>
        </article>

        <article className="card">
          <h3>Verziók</h3>
          <ul className="timeline" data-testid="versions">
            {data.versions.map((version) => (
              <li key={version.version}>
                <time>{asMoment(version.created_at)}</time>
                {version.version}. verzió
                <span className="tag">
                  {version.source === 'staff_editor' ? 'szerkesztő' : 'vendégoldal'}
                </span>
                {version.is_current
                  ? <span className="tag ok">érvényes</span>
                  : <span className="tag stop">ÉRVÉNYTELEN</span>}
                {version.reason !== null && <div className="tag">{version.reason}</div>}
              </li>
            ))}
          </ul>
        </article>
      </div>

      <article className="card" id="levelek">
        <h3>Kiküldött e-mailek (§11.5)</h3>
        {data.outbox.length === 0
          ? <p className="note">Még nincs kiküldött üzenet.</p>
          : (
            <table data-testid="outbox">
              <thead>
                <tr>
                  <th>Kód</th>
                  <th>Címzett</th>
                  <th>Tárgy</th>
                  <th>Állapot</th>
                  <th>Küldve</th>
                  <th>Szolgáltatói azonosító</th>
                </tr>
              </thead>
              <tbody>
                {data.outbox.map((row) => (
                  <tr key={row.id} data-testid={`outbox-${row.event_code}`}>
                    <td>{row.event_code}</td>
                    <td>{row.to_email ?? row.recipient_role ?? '—'}</td>
                    <td>{row.subject ?? '—'}</td>
                    <td data-testid={`outbox-status-${row.id}`}>
                      {outboxStatusLabel(row.status, row.error)}
                      {row.attempts > 1 ? ` · ${fmt.n(row.attempts)} kísérlet` : ''}
                      {row.error !== null && row.status !== 'not_sent' && <div className="tag stop">{row.error}</div>}
                    </td>
                    <td>{asMoment(row.sent_at)}</td>
                    <td className="mono">{row.provider_msg_id ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </article>

      <div className="grid">
        <article className="card" id="penzmozgas">
          <h3>Pénzmozgás</h3>
          {data.payments.length === 0
            ? <p className="note">Még nincs rögzített pénzmozgás.</p>
            : (
              <table data-testid="payments">
                <thead>
                  <tr>
                    <th>Típus</th>
                    <th className="num">Összeg</th>
                    <th>Állapot</th>
                    <th>Hivatkozás</th>
                    <th>Mód</th>
                    <th>Előlegszámla</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{payment.kind}</td>
                      <td className="num">{fmt.money(payment.amount)}</td>
                      <td>
                        {payment.status}
                        {payment.done_at !== null && (
                          <div className="tag ok">{asMoment(payment.done_at)}</div>
                        )}
                      </td>
                      <td>{payment.provider_ref ?? '—'}</td>
                      <td data-testid={`payment-method-${payment.id}`}>{methodLabel(payment.method)}</td>
                      <td>{payment.szamlazz_number ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          {canMarkTransfer && (
            <form
              onSubmit={async (event) => {
                event.preventDefault()
                const ok = method === 'card_dijbekero'
                  ? await run('card_dijbekero_received', {
                    order_id: order.id,
                    amount: Number(amount),
                    payment_date: creditDate,
                    number: advanceNumber,
                  })
                  : await run('transfer_received', {
                    order_id: order.id,
                    amount: Number(amount),
                    bank_ref: bankRef,
                    credit_date: creditDate,
                    ...(numberNeeded ? { number: advanceNumber } : {}),
                  })
                if (ok) {
                  setAmount('')
                  setBankRef('')
                  setAdvanceNumber('')
                  setMethod('')
                  setCreditDate(todayInBudapest())
                }
                // As in the review queue: the database half of §9.6 is
                // committed before the provider is reached, so a refusal the
                // endpoint marked `order_moved` has still left this order
                // somewhere else than the page is showing.
                if (ok || moved.current) reload()
              }}
              data-testid="transfer-form"
            >
              <h3>{lateTransfer ? 'Késve beérkezett fizetés' : 'Fizetés beérkezett'}</h3>
              {lateTransfer && (
                <p className="note warn" data-testid="late-transfer-note">
                  A fizetési határidő lejárt, a rendelés lezárult. Ha a pénz mégis megérkezett,
                  rögzítse: a rendelés visszaigazolásra kerül (késve érkezett fizetés), és a
                  Visszaigazolásra vár nézetben hagyható jóvá vagy utasítható el. Elutasításkor
                  az összeg visszatérítendőként jelenik meg.
                </p>
              )}
              <p className="note">
                A bankban látott összeget rögzítse, ne a rendelését: az eltérést a kiszolgáló
                kezeli (§9.6, F6).
              </p>
              <label htmlFor="payment-method">Fizetés módja</label>
              <select
                id="payment-method"
                required
                value={method}
                onChange={(event) => setMethod(event.target.value as typeof method)}
                data-testid="payment-method"
              >
                <option value="">Válasszon…</option>
                <option value="transfer">Átutalás</option>
                <option value="card_dijbekero">Bankkártya a díjbekérőn</option>
              </select>
              {method === 'transfer' && numberNeeded && (
                <p className="note" data-testid="transfer-dijbekero-note">
                  A díjbekérőre érkezett utalásról a szamlazz.hu maga állítja ki az előlegszámlát: a
                  rendszer nem állít ki másodikat. Írja be a szamlazz.hu-ban látható előlegszámla számát;
                  a rendszer lekéri és ellenőrzi (fajta, bruttó összeg, rendelésszám). Ha az Autokassza
                  nem párosította az utalást, előbb a szamlazz.hu-ban állítsa ki az előlegszámlát a
                  díjbekérőből, és utána rögzítse a számát.
                </p>
              )}
              {method === 'card_dijbekero' && (
                <p className="note" data-testid="card-dijbekero-note">
                  A szamlazz.hu a kártyás fizetéskor maga állítja ki az előlegszámlát: a rendszer nem
                  állít ki másodikat. Írja be a szamlazz.hu-ban látható előlegszámla számát; a rendszer
                  lekéri és ellenőrzi (fajta, bruttó összeg, rendelésszám).
                </p>
              )}
              <label htmlFor="transfer-amount">Beérkezett összeg (Ft)</label>
              <input
                id="transfer-amount"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                data-testid="transfer-amount"
              />
              {method === 'transfer' && numberNeeded && (
                <>
                  <label htmlFor="advance-number">szamlazz.hu előlegszámla száma</label>
                  <input
                    id="advance-number"
                    type="text"
                    required
                    placeholder="PCOL-2026-…"
                    value={advanceNumber}
                    onChange={(event) => setAdvanceNumber(event.target.value)}
                    data-testid="advance-number"
                  />
                </>
              )}
              {method === 'card_dijbekero' ? (
                <>
                  <label htmlFor="advance-number">szamlazz.hu előlegszámla száma</label>
                  <input
                    id="advance-number"
                    type="text"
                    required
                    placeholder="PCOL-2026-…"
                    value={advanceNumber}
                    onChange={(event) => setAdvanceNumber(event.target.value)}
                    data-testid="advance-number"
                  />
                </>
              ) : (
                <>
                  <label htmlFor="transfer-ref">Banki hivatkozás</label>
                  <input
                    id="transfer-ref"
                    type="text"
                    value={bankRef}
                    onChange={(event) => setBankRef(event.target.value)}
                    data-testid="transfer-ref"
                  />
                </>
              )}
              <label htmlFor="transfer-credit-date">
                {method === 'card_dijbekero' ? 'Fizetés napja' : 'Jóváírás napja'}
              </label>
              <input
                id="transfer-credit-date"
                type="date"
                required
                max={today}
                value={creditDate}
                onChange={(event) => setCreditDate(event.target.value)}
                data-testid="transfer-credit-date"
              />
              <div className="actions">
                <button
                  type="submit"
                  className="primary"
                  disabled={busy || method === '' || amount.trim() === '' || creditDate === '' || creditDate > today ||
                    ((method === 'card_dijbekero' || numberNeeded) && advanceNumber.trim() === '')}
                  data-testid="transfer-send"
                >
                  Rögzítem
                </button>
              </div>
            </form>
          )}
        </article>

        <article className="card" id="szamlak">
          <h3>Számlák (§9.2)</h3>
          {data.invoices.length === 0
            ? <p className="note">Még nincs kiállított bizonylat.</p>
            : (
              <table data-testid="invoices">
                <thead>
                  <tr>
                    <th>Típus</th>
                    <th>Szám</th>
                    <th className="num">Bruttó</th>
                    <th>Állapot</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.kind}</td>
                      <td>{invoice.szamlazz_number ?? '—'}</td>
                      <td className="num">
                        {invoice.gross === null ? '—' : fmt.money(invoice.gross)}
                      </td>
                      <td>
                        {invoice.kind === 'vegszamla' && invoice.status === 'held'
                          ? <span className="tag warn" data-testid="vegszamla-held">végszámla visszatartva</span>
                          : invoice.status}
                        {invoice.error !== null && <div className="tag stop">{invoice.error}</div>}
                        {invoice.issued_by === 'szamlazz' && (
                          <div
                            className={`tag ${invoice.verification === 'verified' ? 'ok' : 'stop'}`}
                            data-testid={`invoice-verification-${invoice.id}`}
                          >
                            szamlazz.hu állította ki ·{' '}
                            {invoice.verification === 'verified' ? 'ellenőrizve' : 'ellenőrzés sikertelen'}
                            {invoice.verification_note ? ` · ${invoice.verification_note}` : ''}
                          </div>
                        )}
                        {!testOrder && invoice.kind === 'vegszamla' && invoice.status === 'held' && (
                          <form
                            onSubmit={async (event) => {
                              event.preventDefault()
                              const ok = await run('vegszamla_done', {
                                invoice_id: invoice.id,
                                number: handNumber[invoice.id] ?? '',
                              })
                              if (ok) reload()
                            }}
                            data-testid={`vegszamla-done-form-${invoice.id}`}
                          >
                            <label htmlFor={`vegszamla-number-${invoice.id}`}>Kézzel kiállított végszámla száma</label>
                            <input
                              id={`vegszamla-number-${invoice.id}`}
                              type="text"
                              value={handNumber[invoice.id] ?? ''}
                              onChange={(event) => setHandNumber({ ...handNumber, [invoice.id]: event.target.value })}
                              data-testid="vegszamla-number"
                            />
                            <button
                              type="submit"
                              disabled={busy || (handNumber[invoice.id] ?? '').trim() === ''}
                              data-testid="vegszamla-done"
                            >
                              Kész
                            </button>
                          </form>
                        )}
                      </td>
                      <td>
                        {invoice.pdf_path === null
                          ? <span className="note">—</span>
                          : (
                            <button
                              type="button"
                              onClick={() => void openInvoicePdf(invoice.id)}
                              disabled={pdfBusy !== null}
                              data-testid={`invoice-pdf-${invoice.kind}`}
                            >
                              Megnyitom
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          {pdfError !== null && <p className="tag stop">{pdfError}</p>}
          <p className="note">
            A bizonylatok PDF-je zárt tárolóban van; csak bejelentkezett munkatárs nyithatja meg, innen.
          </p>
        </article>
      </div>

      <article className="card" id="modositasok">
        <h3>Módosítási kérések (§11.8)</h3>
        {data.change_requests.length === 0
          ? <p className="note">Nincs módosítási kérés.</p>
          : (
            <ul className="timeline">
              {data.change_requests.map((request) => (
                <li key={request.id}>
                  <time>{asMoment(request.received_at)}</time>
                  {request.kind} · {request.status}
                </li>
              ))}
            </ul>
          )}
        <p className="note" data-testid="editor-link">
          A módosítás rögzítése és alkalmazása, a díj elengedése és az új verzió összeállítása a
          rendelésszerkesztőben történik (§11.8, §11.11).{' '}
          <Link to={`/staff/editor?order=${data.order.id}`} data-testid="open-editor">
            Megnyitás a szerkesztőben
          </Link>
        </p>
      </article>
    </section>
  )
}

/**
 * Run 12 item 2d (DECIDED 20261011): every message shows as sent, failed or
 * not sent. A message the allow-list held reads "not sent: not on
 * allow-list" — the reason is the row's own, so a later reason reads as
 * itself. The other statuses are the outbox's own words.
 */
export function outboxStatusLabel(status: string, error: string | null): string {
  if (status === 'not_sent') return `not sent: ${error ?? 'held'}`
  return status
}

/** Card payment change 4: the payment history names how the money came. */
function methodLabel(method: string | null | undefined): string {
  if (method === 'card_dijbekero') return 'bankkártya (díjbekérő)'
  if (method === 'transfer') return 'átutalás'
  return '—'
}
